// HelpGuides.tsx · v12.10.25.3 (#2106 search shows matching articles + guides at the top; 📖 Read)
// HelpGuides.tsx · v12.10.25 (#2106 the Help landing - search box + Browse all articles, then the guides list) · 2026-10-30 · VoiceSpectrum AI
// LOCKED prototype UI-PROTO-FIRST-RUN-T180_v1.4 (Tushar 10/30): the Help page opens with a highlighted search box across the
// top ("Browse all articles ›" at its right end opens the library), then "Guides": one row per guide the person's rights can
// open - ▶ Play guide (the spotlight tour on the real screens) and ✓ Checklist (the "Let's get started" pill) + progress.
import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../../lib/useAccess';
import { checkOf, rightsFrom, sendFirstRun, useFirstRun, visibleGuides, type Guide, type GuideKey } from '../../lib/firstRun';
import '../../components/firstrun/firstrun.css';

// v12.10.25.3 (Tushar 10/30): results = the matching articles, shown right under the search box; match = which guides a search keeps;
// onRead = the third button (the guide's articles in the in-app drawer)
export default function HelpGuides({ query, onQuery, onBrowse, results, match, onRead }: { query: string; onQuery: (q: string) => void; onBrowse: () => void;
  results?: ReactNode; match?: (g: Guide) => boolean; onRead?: (k: GuideKey) => void }) {
  const { me } = useAccess();
  const can = useMemo(() => rightsFrom(me?.access as Record<string, string[] | undefined> | undefined), [me]);
  const { data } = useFirstRun();
  const navigate = useNavigate();
  const guides = visibleGuides(can).filter((g) => !match || match(g));

  // Help is its own tab: the guide plays in THIS tab - leave Help for the app and let the Shell's host run it.
  // '/' = the person's own home screen (Messages, or the first screen their user group opens); the tour then goes where it needs.
  function play(k: GuideKey) { navigate('/'); window.setTimeout(() => sendFirstRun({ action: 'tour', guide: k }), 80); }
  function checklist(k: GuideKey) { navigate('/'); window.setTimeout(() => sendFirstRun({ action: 'checklist', guide: k }), 80); }

  return (
    <div className="fr-help" data-testid="help-guides">
      <h1>Help</h1>
      <div className="fr-hsearch">
        <span aria-hidden="true">🔍</span>
        <input type="search" value={query} onChange={(e) => onQuery(e.target.value)} aria-label="Search help"
          placeholder="Search help - e.g. mark complete, follow-up, keypad menus, add a user" />
        <button type="button" onClick={onBrowse}>Browse all articles ›</button>
      </div>
      {results}
      {guides.length > 0 && <div className="fr-hsec">Guides <span>· play one on your own screens, read its articles, or work through its checklist</span></div>}
      {guides.map((g) => {
        const items = checkOf(g, can);
        const done = items.filter((c) => (data?.progress[g.key] ?? []).includes(c.key)).length;
        const all = items.length > 0 && done === items.length;
        return (
          <div key={g.key} className="fr-grow">
            <span className="fr-big" aria-hidden="true">{g.icon}</span>
            <div className="fr-tx">
              <b>{g.name}</b> <span className="fr-len">· {g.len}</span>{all && <span className="fr-badge">Done</span>}
              <p>{g.desc}</p>
            </div>
            <span className={`fr-pg${all ? ' done' : ''}`}>{done} of {items.length}{all ? ' ✓' : ''}</span>
            <button type="button" className="fr-btn p" onClick={() => play(g.key)}>▶ Play guide</button>
            {onRead && <button type="button" className="fr-btn" onClick={() => onRead(g.key)}>📖 Read</button>}
            <button type="button" className="fr-btn" onClick={() => checklist(g.key)}>✓ Checklist</button>
          </div>
        );
      })}
    </div>
  );
}
